






































































































































































import { Component, Vue } from "vue-property-decorator";
import { makeGeoEncodeRequest } from "@/utils/GeocodingService";

/* global google */

const GOOGLE_MAPS_API_KEY = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "";

/**
 * Loads the Google Maps JavaScript API by injecting a script tag.
 * Resolves when the API is fully ready with all constructors available.
 */
function loadGoogleMapsApi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.Map) {
      resolve();
      return;
    }

    const existing = document.getElementById("google-maps-script");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps API"));
    document.head.appendChild(script);
  });
}

@Component
export default class MapsPageComponent extends Vue {
  map: google.maps.Map | null = null;
  markers: google.maps.Marker[] = [];
  routeRenderers: google.maps.DirectionsRenderer[] = [];
  directionsService: google.maps.DirectionsService | null = null;
  startAutocomplete: google.maps.places.Autocomplete | null = null;
  destinationAutocomplete: google.maps.places.Autocomplete | null = null;

  location = "";
  postCode = "";
  street = "";
  streetNumber = "";
  startAddress = "";
  destinationAddress = "";
  output = "";
  routeInformation = "";
  errorText = "";
  showError = false;
  isGeocoding = false;
  isRouting = false;

  async mounted() {
    await loadGoogleMapsApi();
    this.initMap();
    this.initAutocomplete();
  }

  initMap() {
    const container = this.$refs.mapContainer as HTMLElement;
    this.map = new google.maps.Map(container, {
      center: { lat: 51, lng: 10 },
      zoom: 6,
    });
    this.directionsService = new google.maps.DirectionsService();
  }

  /** Attaches Google Places Autocomplete to the start and destination inputs. */
  initAutocomplete() {
    const startEl = (this.$refs.startInput as Vue)?.$el?.querySelector("input");
    const destEl = (this.$refs.destinationInput as Vue)?.$el?.querySelector("input");

    if (startEl) {
      this.startAutocomplete = new google.maps.places.Autocomplete(startEl, {
        fields: ["formatted_address"],
      });
      this.startAutocomplete.addListener("place_changed", () => {
        const place = this.startAutocomplete?.getPlace();
        if (place?.formatted_address) {
          this.startAddress = place.formatted_address;
        }
      });
    }

    if (destEl) {
      this.destinationAutocomplete = new google.maps.places.Autocomplete(destEl, {
        fields: ["formatted_address"],
      });
      this.destinationAutocomplete.addListener("place_changed", () => {
        const place = this.destinationAutocomplete?.getPlace();
        if (place?.formatted_address) {
          this.destinationAddress = place.formatted_address;
        }
      });
    }
  }

  /** Geocodes the entered address and places a marker on the map. */
  async geocodeAddress() {
    const city = this.location;
    const zipCode = this.postCode;
    const streetName = this.street;
    const houseNumber = this.streetNumber;

    if (!city && !zipCode && !streetName) {
      this.errorText = this.$t("maps.noAddress").toString();
      this.showError = true;
      return;
    }

    this.isGeocoding = true;
    try {
      const result = await makeGeoEncodeRequest(
        streetName,
        houseNumber,
        city,
        zipCode,
        ""
      );

      if (result && this.map) {
        const position = { lat: result.latitude, lng: result.longitude };
        this.addMarker(position);
        this.map.panTo(position);
        this.map.setZoom(14);
        this.output = `${result.latitude}, ${result.longitude}`;
      } else {
        this.errorText = this.$t("maps.geocodingFailed").toString();
        this.showError = true;
      }
    } finally {
      this.isGeocoding = false;
    }
  }

  /** Calculates and displays a driving route between start and destination. */
  calculateRoute() {
    if (!this.startAddress || !this.destinationAddress) {
      this.errorText = this.$t("maps.routeMissingAddresses").toString();
      this.showError = true;
      return;
    }

    if (!this.directionsService) return;

    this.isRouting = true;
    this.directionsService.route(
      {
        origin: this.startAddress,
        destination: this.destinationAddress,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        this.isRouting = false;
        if (status === google.maps.DirectionsStatus.OK && result) {
          const renderer = new google.maps.DirectionsRenderer({
            map: this.map,
            directions: result,
          });
          this.routeRenderers.push(renderer);

          const leg = result.routes[0].legs[0];
          this.routeInformation = `${this.$t("maps.distance")}: ${leg.distance?.text} — ${this.$t("maps.duration")}: ${leg.duration?.text}`;
        } else {
          this.errorText = this.$t("maps.routeFailed").toString();
          this.showError = true;
        }
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addMarker(position: { lat: number; lng: number }) {
    const marker = new google.maps.Marker({
      position,
      map: this.map,
    });
    this.markers.push(marker);
  }

  clearMarkers() {
    for (const marker of this.markers) {
      marker.setMap(null);
    }
    this.markers = [];
  }

  clearRoutes() {
    for (const renderer of this.routeRenderers) {
      renderer.setMap(null);
    }
    this.routeRenderers = [];
    this.routeInformation = "";
  }
}
